import { getRequest, postRequest } from "../../../utils/requests";
import config from "../../../config.json";

const BASE = (process.env.REACT_APP_API_BASE || `${config.service}/api`).replace(/\/$/, "");

/**
 * Получение доступных внутренних номеров Sipuni
 * @returns {Promise<Object>} Список доступных номеров
 */
export async function getAvailableExtensions() {
  try {
    const response = await getRequest(`${BASE}/sipuni/admin/available-extensions`);
    return response;
  } catch (error) {
    console.error('Ошибка загрузки доступных номеров:', error);
    throw error;
  }
}

/**
 * Получение предложений внутреннего номера по телефону и имени
 * @param {string} phone - Номер телефона пользователя
 * @param {string} userName - Имя пользователя
 * @returns {Promise<Object>} Предложения системы
 */
export async function suggestExtensionByPhone(phone, userName = '') {
  try {
    const response = await postRequest(`${BASE}/sipuni/admin/suggest-by-phone`, {
      phone,
      userName
    });
    return response;
  } catch (error) {
    console.error('Ошибка получения предложений:', error);
    throw error;
  }
}

/**
 * Проверка доступности внутреннего номера
 * @param {string} extension - Внутренний номер для проверки
 * @returns {Promise<Object>} Результат проверки
 */
export async function checkExtensionAvailability(extension) {
  try {
    const response = await getRequest(`${BASE}/sipuni/admin/check-extension/${extension}`);
    return response;
  } catch (error) {
    console.error('Ошибка проверки номера:', error);
    throw error;
  }
}

/**
 * Получение информации о пользователе по внутреннему номеру
 * @param {string} extension - Внутренний номер
 * @returns {Promise<Object>} Информация о пользователе
 */
export async function getUserByExtension(extension) {
  try {
    const response = await getRequest(`${BASE}/sipuni/admin/user-by-extension/${extension}`);
    return response;
  } catch (error) {
    console.error('Ошибка получения пользователя по номеру:', error);
    throw error;
  }
}

/**
 * Обновление настроек Sipuni для пользователя
 * @param {string} userId - ID пользователя
 * @param {Object} sipuniSettings - Настройки Sipuni
 * @returns {Promise<Object>} Результат обновления
 */
export async function updateUserSipuniSettings(userId, sipuniSettings) {
  try {
    const response = await postRequest(`${BASE}/sipuni/admin/user/${userId}/settings`, sipuniSettings);
    return response;
  } catch (error) {
    console.error('Ошибка обновления настроек Sipuni:', error);
    throw error;
  }
}

/**
 * Получение статистики использования внутренних номеров
 * @returns {Promise<Object>} Статистика номеров
 */
export async function getExtensionsStats() {
  try {
    const response = await getRequest(`${BASE}/sipuni/admin/extensions-stats`);
    return response;
  } catch (error) {
    console.error('Ошибка получения статистики номеров:', error);
    throw error;
  }
}

/**
 * Генерация простых предложений номеров на основе доступных
 * @param {Array} availableExtensions - Доступные номера
 * @param {string} phone - Номер телефона (для анализа)
 * @param {string} userName - Имя пользователя (для анализа)
 * @returns {Array} Массив предложений
 */
export function generateSimpleExtensionSuggestions(availableExtensions, phone = '', userName = '') {
  if (!Array.isArray(availableExtensions) || availableExtensions.length === 0) {
    return [];
  }

  const suggestions = [];
  
  // Алгоритм простых предложений:
  
  // 1. Если в имени есть цифры, попробуем найти похожий номер
  const nameNumbers = userName.match(/\d+/g);
  if (nameNumbers && nameNumbers.length > 0) {
    for (const num of nameNumbers) {
      const paddedNum = num.padStart(3, '1'); // 1 -> 101, 23 -> 123
      if (availableExtensions.includes(paddedNum)) {
        suggestions.push({
          value: paddedNum,
          confidence: 0.8,
          description: `Номер ${paddedNum} содержит цифры из имени`
        });
      }
    }
  }

  // 2. Если в телефоне есть повторяющиеся цифры
  const phoneDigits = phone.replace(/\D/g, ''); // только цифры
  const lastThreeDigits = phoneDigits.slice(-3);
  if (availableExtensions.includes(lastThreeDigits) && lastThreeDigits.length === 3) {
    suggestions.push({
      value: lastThreeDigits,
      confidence: 0.7,
      description: `Номер ${lastThreeDigits} из последних цифр телефона`
    });
  }

  // 3. Популярные начальные номера для новых сотрудников
  const popularStarters = ['101', '102', '103', '201', '202', '203'];
  for (const starter of popularStarters) {
    if (availableExtensions.includes(starter) && !suggestions.find(s => s.value === starter)) {
      suggestions.push({
        value: starter,
        confidence: 0.6,
        description: `Популярный стартовый номер ${starter}`
      });
      
      if (suggestions.length >= 3) break; // Максимум 3 предложения
    }
  }

  // 4. Если предложений мало, добавляем первые доступные
  while (suggestions.length < 3 && suggestions.length < availableExtensions.length) {
    const nextAvailable = availableExtensions[suggestions.length];
    if (!suggestions.find(s => s.value === nextAvailable)) {
      suggestions.push({
        value: nextAvailable,
        confidence: 0.5,
        description: `Доступный номер ${nextAvailable}`
      });
    }
  }

  return suggestions.slice(0, 3); // Максимум 3 предложения
}

/**
 * Валидация внутреннего номера
 * @param {string} extension - Номер для валидации
 * @returns {Object} Результат валидации
 */
export function validateExtension(extension) {
  const result = {
    isValid: false,
    errors: []
  };

  if (!extension) {
    result.errors.push('Номер не может быть пустым');
    return result;
  }

  // Проверка формата (3-4 цифры)
  if (!/^\d{3,4}$/.test(extension)) {
    result.errors.push('Номер должен содержать 3-4 цифры');
  }

  // Проверка диапазона
  const num = parseInt(extension);
  if (num < 100 || num > 9999) {
    result.errors.push('Номер должен быть в диапазоне от 100 до 9999');
  }

  // Проверка запрещенных номеров (системные)
  const forbiddenNumbers = ['911', '112', '000', '999'];
  if (forbiddenNumbers.includes(extension)) {
    result.errors.push('Номер зарезервирован системой');
  }

  result.isValid = result.errors.length === 0;
  return result;
}

/**
 * Форматирование внутреннего номера
 * @param {string} extension - Номер для форматирования
 * @returns {string} Отформатированный номер
 */
export function formatExtension(extension) {
  if (!extension) return '';
  
  // Убираем все нецифровые символы
  const digits = extension.replace(/\D/g, '');
  
  // Ограничиваем до 4 цифр
  return digits.slice(0, 4);
}

/**
 * Обработка ошибок API Sipuni для пользователей
 * @param {Error} error - Ошибка
 * @returns {string} Понятное сообщение об ошибке
 */
export function handleSipuniUserApiError(error) {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error?.response?.status === 409) {
    return 'Внутренний номер уже используется другим пользователем';
  }
  
  if (error?.response?.status === 404) {
    return 'Внутренний номер не найден в системе';
  }
  
  if (error?.response?.status === 400) {
    return 'Неверный формат внутреннего номера';
  }
  
  return error?.message || 'Ошибка при работе с внутренними номерами';
}
